import {
	FieldValues,
	FieldPath,
	useController,
	UseControllerProps,
} from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers';
import { FormControl, FormHelperText, TextField } from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import dayjs from 'dayjs';
import {
	API_DATE_FORMAT,
	DISPLAY_DATE_FORMAT,
} from '../../../utils/dateHelper';

interface AppDatePickerProps {
	label?: string;
	minimumDate?: string;
	maximumDate?: string;
	placeholder?: string;
	disabled?: boolean;
	formatDate?: string;
	handleChange?: any;
	sx?: any;
	variantType?: string;
}

export default function AppDatePicker<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
	label,
	minimumDate,
	maximumDate,
	placeholder,
	disabled,
	handleChange,
	variantType,
	sx,
	...props
}: AppDatePickerProps & UseControllerProps<TFieldValues, TName>) {
	const { field, fieldState } = useController(props);
	const { formatDate } = props;
	const errorMessage = fieldState.error?.message;

	return (
		<DatePicker
			enableAccessibleFieldDOMStructure={false}
			disabled={disabled}
			{...field}
			format={DISPLAY_DATE_FORMAT}
			minDate={
				minimumDate === '' || minimumDate === undefined
					? undefined
					: dayjs(minimumDate, formatDate ? formatDate : API_DATE_FORMAT)
			}
			maxDate={
				maximumDate === '' || maximumDate === undefined
					? undefined
					: dayjs(maximumDate, formatDate ? formatDate : API_DATE_FORMAT)
			}
			value={
				field.value === '' || field.value === undefined || field.value === null
					? null
					: dayjs(field.value, formatDate ? formatDate : API_DATE_FORMAT)
			}
			onChange={(value) => {
				if (value === null) {
					field.onChange(null);
				} else
					field.onChange(
						dayjs(value).format(formatDate ? formatDate : API_DATE_FORMAT)
					);
				handleChange && handleChange(value);
			}}
			slots={{
				textField: (params) => (
					<FormControl fullWidth error={!!errorMessage} sx={sx}>
						<TextField
							variant={variantType ?? 'outlined'}
							{...params}
							label={label}
							error={!!errorMessage}
							inputProps={{
								...params.inputProps,
								placeholder: placeholder || params.inputProps.placeholder,
							}}
						/>
						<FormHelperText>{errorMessage}</FormHelperText>
					</FormControl>
				),
				openPickerIcon: fieldState.error
					? EventBusyOutlinedIcon
					: CalendarTodayOutlinedIcon,
			}}
			slotProps={{
				textField: {
					fullWidth: true,
				},
			}}
		/>
	);
}
